import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, Edit2, Clock, FileText, Link as LinkIcon, ClipboardList,
  CheckCircle, PackageCheck, Plus, Trash2, MessageSquare, Send, Loader2,
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { toastApiError } from '@/lib/errors';
import StatusBadge from '@/components/StatusBadge';
import RefLink from '@/components/RefLink';
import EmptyState from '@/components/EmptyState';
import LoadingState from '@/components/LoadingState';

// Order detail page.
// Route: /orders/:id
// Tabs: Přehled / Historie / Související
// Read-only zobrazení; editace probíhá v /orders split-view.

const TABS_BASE = [
  { id: 'overview', labelKey: 'orders.detail-tab-overview', icon: ClipboardList },
  { id: 'chat', labelKey: 'orders.detail-tab-chat', icon: MessageSquare, requiresPortalOrder: true },
  { id: 'history', labelKey: 'orders.detail-tab-history', icon: Clock },
  { id: 'related', labelKey: 'orders.detail-tab-related', icon: LinkIcon },
];

function TabButton({ tab, active, onClick }) {
  const { t } = useTranslation();
  const Icon = tab.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors relative"
      style={{
        color: active ? 'var(--ct-accent)' : 'var(--ct-text-secondary)',
        borderBottom: active ? '2px solid var(--ct-accent)' : '2px solid transparent',
      }}
    >
      <Icon className="w-4 h-4" />
      {t(tab.labelKey)}
    </button>
  );
}

function TimelineEvent({ icon: Icon, tone, at, title, description }) {
  const { t } = useTranslation();
  const toneColors = {
    info: 'var(--ct-info)',
    success: 'var(--ct-success)',
    warning: 'var(--ct-warning)',
  };
  const color = toneColors[tone] || 'var(--ct-text-secondary)';
  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: 'var(--ct-bg-secondary)', border: `2px solid ${color}` }}
        >
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <div className="flex-1 w-px mt-1" style={{ background: 'var(--ct-border)' }} />
      </div>
      <div className="flex-1 pb-5 -mt-0.5">
        <div className="text-sm font-medium" style={{ color: 'var(--ct-text)' }}>{title}</div>
        {description && (
          <div className="text-xs mt-0.5" style={{ color: 'var(--ct-text-secondary)' }}>{description}</div>
        )}
        {at && (
          <div className="text-xs font-mono mt-1" style={{ color: 'var(--ct-text-disabled)' }}>
            {new Date(at).toLocaleString(t('common.date-locale'))}
          </div>
        )}
      </div>
    </div>
  );
}

function OverviewTab({ order }) {
  const { t } = useTranslation();
  const customer = order.customer || {};
  const instruments = order.instruments || [];
  return (
    <div className="space-y-6">
      {/* Customer block */}
      <section>
        <h3 className="ct-eyebrow mb-2">{t('orders.section-customer')}</h3>
        <div className="ct-panel p-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="ct-eyebrow">{t('orders.label-company')}</div>
              <div style={{ color: 'var(--ct-text)' }}>{customer.company || '—'}</div>
            </div>
            <div>
              <div className="ct-eyebrow">{t('orders.label-contact-person')}</div>
              <div style={{ color: 'var(--ct-text)' }}>{customer.contactPerson || '—'}</div>
            </div>
            <div>
              <div className="ct-eyebrow">{t('orders.label-email')}</div>
              <div className="font-mono text-xs" style={{ color: 'var(--ct-text)' }}>{customer.email || '—'}</div>
            </div>
            <div>
              <div className="ct-eyebrow">{t('orders.label-phone')}</div>
              <div className="font-mono text-xs" style={{ color: 'var(--ct-text)' }}>{customer.phone || '—'}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Instruments */}
      <section>
        <h3 className="ct-eyebrow mb-2">{t('orders.section-instruments', { count: instruments.length })}</h3>
        {instruments.length === 0 ? (
          <div className="ct-panel p-4">
            <div className="text-sm italic" style={{ color: 'var(--ct-text-disabled)' }}>{t('orders.detail-no-instruments')}</div>
          </div>
        ) : (
          <div className="ct-panel overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: 'var(--ct-bg-secondary)' }}>
                  <th className="text-left py-2 px-3 ct-eyebrow">{t('orders.instr-col-name')}</th>
                  <th className="text-left py-2 px-3 ct-eyebrow">{t('orders.instr-col-type')}</th>
                  <th className="text-left py-2 px-3 ct-eyebrow">{t('orders.instr-col-manufacturer')}</th>
                  <th className="text-left py-2 px-3 ct-eyebrow">{t('orders.detail-col-customer-labs')}</th>
                  <th className="text-left py-2 px-3 ct-eyebrow">{t('orders.instr-col-marking-1')}</th>
                  <th className="text-left py-2 px-3 ct-eyebrow">{t('orders.instr-col-marking-2')}</th>
                </tr>
              </thead>
              <tbody>
                {instruments.map((inst, idx) => (
                  <tr key={idx} style={{ borderTop: '1px solid var(--ct-border)' }}>
                    <td className="py-2 px-3">{inst.name}</td>
                    <td className="py-2 px-3 font-mono text-xs">{inst.type}</td>
                    <td className="py-2 px-3">{inst.manufacturer}</td>
                    <td className="py-2 px-3">
                      {inst.customerLaboratories && inst.customerLaboratories.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {inst.customerLaboratories.map((lab) => (
                            <span
                              key={lab}
                              className="px-2 py-0.5 text-xs rounded"
                              style={{ background: 'var(--ct-bg-secondary)', color: 'var(--ct-accent)' }}
                              title={t('orders.detail-customer-lab-tooltip')}
                            >
                              {lab}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: 'var(--ct-text-disabled)' }}>—</span>
                      )}
                    </td>
                    <td className="py-2 px-3 font-mono text-xs">{inst.marking1 || '—'}</td>
                    <td className="py-2 px-3 font-mono text-xs">{inst.marking2 || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Metadata */}
      {(order.transport || order.notes) && (
        <section>
          <h3 className="ct-eyebrow mb-2">{t('orders.detail-section-extra')}</h3>
          <div className="ct-panel p-4 space-y-3 text-sm">
            {order.transport && (
              <div>
                <div className="ct-eyebrow">{t('orders.detail-label-transport')}</div>
                <div style={{ color: 'var(--ct-text)' }}>{order.transport}</div>
              </div>
            )}
            {order.notes && (
              <div>
                <div className="ct-eyebrow">{t('orders.detail-label-notes')}</div>
                <div style={{ color: 'var(--ct-text)', whiteSpace: 'pre-wrap' }}>{order.notes}</div>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

function HistoryTab({ order }) {
  const { t } = useTranslation();
  // Timeline syntetizovaná z dostupných timestampů.
  const events = [
    order.createdAt && {
      icon: Plus,
      tone: 'info',
      at: order.createdAt,
      title: t('orders.toast-created'),
      description: t('orders.detail-history-created-desc', { number: order.orderNumber }),
    },
    order.confirmedAt && {
      icon: CheckCircle,
      tone: 'warning',
      at: order.confirmedAt,
      title: t('orders.toast-confirmed'),
      description: t('orders.detail-history-confirmed-desc'),
    },
    order.receivedAt && {
      icon: PackageCheck,
      tone: 'success',
      at: order.receivedAt,
      title: t('orders.toast-received'),
      description: t('orders.detail-history-received-desc'),
    },
  ].filter(Boolean);

  if (events.length === 0) {
    return <EmptyState icon={Clock} title={t('orders.detail-history-empty')} description={t('orders.detail-history-empty-desc')} />;
  }

  return (
    <div className="ct-panel p-4">
      {events.map((e, idx) => (
        <TimelineEvent key={idx} {...e} />
      ))}
    </div>
  );
}

function RelatedTab({ order, intake, calibrations }) {
  const { t } = useTranslation();
  const hasIntake = Boolean(intake);
  const hasCalibrations = calibrations.length > 0;

  if (!hasIntake && !hasCalibrations) {
    return (
      <EmptyState
        icon={LinkIcon}
        title={t('orders.detail-related-empty')}
        description={
          order.status === 'new'
            ? t('orders.detail-related-empty-new')
            : order.status === 'confirmed'
            ? t('orders.detail-related-empty-confirmed')
            : t('orders.detail-related-empty-other')
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      {hasIntake && (
        <section>
          <h3 className="ct-eyebrow mb-2">{t('orders.detail-section-intake')}</h3>
          <div className="ct-panel p-4 flex items-center justify-between gap-3">
            <div className="min-w-0 flex items-center gap-3">
              <RefLink type="intake" id={intake.intakeProtocolNumber || intake.protocolNumber} />
              <span className="text-xs" style={{ color: 'var(--ct-text-secondary)' }}>
                {(intake.instruments || []).length} přístrojů ·
                {' '}{intake.receivedAt ? new Date(intake.receivedAt).toLocaleDateString(t('common.date-locale')) : ''}
              </span>
            </div>
            <StatusBadge
              tone={intake.sentToCalibration ? 'success' : 'warning'}
              className="flex-shrink-0"
            >
              {intake.sentToCalibration ? t('orders.intake-status-sent') : t('orders.intake-status-ready')}
            </StatusBadge>
          </div>
        </section>
      )}

      {hasCalibrations && (
        <section>
          <h3 className="ct-eyebrow mb-2">{t('orders.related-calibrations-title', { count: calibrations.length })}</h3>
          <div className="ct-panel overflow-hidden">
            {calibrations.map((c, idx) => (
              <div
                key={c.id}
                className="px-4 py-2.5 text-sm flex items-center justify-between"
                style={{ borderTop: idx === 0 ? 'none' : '1px solid var(--ct-border)' }}
              >
                <div className="min-w-0 flex-1 truncate">
                  <span style={{ color: 'var(--ct-text)' }}>{c.instrumentName || '—'}</span>
                  <span className="text-xs ml-2" style={{ color: 'var(--ct-text-secondary)' }}>
                    {c.laboratory} {c.methodologyCode ? `· ${c.methodologyCode}` : ''}
                  </span>
                </div>
                <StatusBadge status={c.status} className="flex-shrink-0" />
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// Issue #3 — chat se zákazníkem (lab-side UI, sdílená collection order_messages s portálem).
function ChatTab({ orderId, customerName }) {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const reload = () => {
    api.get(`/orders/${orderId}/messages`)
      .then(r => setMessages(Array.isArray(r.data) ? r.data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
    // Polling každých 8s — WS broadcast je best-effort jen pro portál, lab si refreshuje sám.
    const iv = setInterval(reload, 8000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const send = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setSending(true);
    try {
      await api.post(`/orders/${orderId}/messages`, { message: trimmed });
      setText('');
      reload();
    } catch (e) {
      toastApiError(e, t('orders.chat-error-send'));
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="text-sm" style={{ color: 'var(--ct-text-secondary)' }}>{t('orders.chat-loading')}</div>;

  return (
    <div className="ct-panel flex flex-col" style={{ height: 'calc(100vh - 16rem)', minHeight: 400 }}>
      {/* Message list */}
      <ScrollArea className="flex-1 p-4">
        {messages.length === 0 ? (
          <EmptyState
            icon={MessageSquare}
            title={t('orders.chat-empty')}
            description={t('orders.chat-empty-desc', { name: customerName || t('orders.chat-empty-fallback-name') })}
            size="sm"
          />
        ) : (
          <div className="space-y-3">
            {messages.map(m => {
              const isLab = m.userType === 'lab';
              return (
                <div key={m.id} className={`flex ${isLab ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className="max-w-[75%] px-3 py-2 rounded-lg text-sm"
                    style={{
                      background: isLab ? 'var(--ct-accent)' : 'var(--ct-bg-secondary)',
                      color: isLab ? '#fff' : 'var(--ct-text)',
                    }}
                  >
                    <div className="text-xs opacity-80 mb-0.5">
                      {isLab ? t('orders.chat-author-lab') : (m.userName || t('orders.chat-author-customer'))}
                      {' · '}
                      {m.sentAt ? new Date(m.sentAt).toLocaleString(t('common.date-locale')) : ''}
                    </div>
                    <div className="whitespace-pre-wrap">{m.message}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>
      {/* Input */}
      <div className="border-t p-3 flex gap-2 items-end" style={{ borderColor: 'var(--ct-border)' }}>
        <Textarea
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder={t('orders.chat-input-placeholder')}
          className="text-sm min-h-[60px] resize-none"
          disabled={sending}
        />
        <Button
          onClick={send}
          disabled={!text.trim() || sending}
          className="ct-btn-primary gap-1"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {t('orders.chat-btn-send')}
        </Button>
      </div>
    </div>
  );
}

export default function OrderDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [intake, setIntake] = useState(null);
  const [calibrations, setCalibrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    api.get(`/orders/${id}`)
      .then(r => { if (!cancelled) setOrder(r.data); })
      .catch(err => { if (!cancelled) setNotFound(err.response?.status === 404); })
      .finally(() => { if (!cancelled) setLoading(false); });

    // Related entities — paralelně s order fetchem.
    api.get('/intakes').then(r => {
      if (cancelled) return;
      setIntake(r.data.find(i => i.orderId === id) || null);
    }).catch(() => {});
    api.get('/calibrations').then(r => {
      if (cancelled) return;
      setCalibrations(r.data.filter(c => c.orderId === id));
    }).catch(() => {});

    return () => { cancelled = true; };
  }, [id]);

  if (loading) return <LoadingState message={t('orders.loading-page')} />;
  if (notFound || !order) {
    return (
      <EmptyState
        icon={ClipboardList}
        title={t('orders.not-found-title')}
        description={t('orders.not-found-desc')}
        action={<Button onClick={() => navigate('/orders')} className="ct-btn-secondary">{t('orders.btn-back-list')}</Button>}
      />
    );
  }

  return (
    <div data-testid="order-detail-page" className="space-y-4 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => navigate('/orders')}
            className="flex items-center gap-1 text-sm transition-colors hover:opacity-80"
            style={{ color: 'var(--ct-text-secondary)' }}
            title={t('orders.btn-back-list-title')}
          >
            <ArrowLeft className="w-4 h-4" />
            {t('orders.btn-back-list-label')}
          </button>
          <div className="flex items-baseline gap-3 min-w-0">
            <h1 className="ct-h1 font-mono truncate" style={{ color: 'var(--ct-accent)' }}>
              {order.orderNumber}
            </h1>
            <span className="text-sm truncate" style={{ color: 'var(--ct-text-secondary)' }}>
              {order.customer?.company || t('orders.list-no-customer')}
            </span>
          </div>
          <StatusBadge status={order.status} className="flex-shrink-0" />
        </div>
        <Link
          to={`/orders?edit=${id}`}
          className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded border transition-colors"
          style={{
            background: 'var(--ct-surface)',
            borderColor: 'var(--ct-border)',
            color: 'var(--ct-text)',
          }}
        >
          <Edit2 className="w-3.5 h-3.5" />
          {t('orders.btn-edit')}
        </Link>
      </div>

      {/* Tabs — chat tab visible jen pro portál objednávky (s portalOrderId). */}
      <div className="flex border-b" style={{ borderColor: 'var(--ct-border)' }}>
        {TABS_BASE.filter(t => !t.requiresPortalOrder || order.portalOrderId).map(t => (
          <TabButton
            key={t.id}
            tab={t}
            active={activeTab === t.id}
            onClick={() => setActiveTab(t.id)}
          />
        ))}
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'overview' && <OverviewTab order={order} />}
        {activeTab === 'chat' && order.portalOrderId && (
          <ChatTab orderId={id} customerName={order.customer?.contactPerson || order.customer?.company} />
        )}
        {activeTab === 'history' && <HistoryTab order={order} />}
        {activeTab === 'related' && <RelatedTab order={order} intake={intake} calibrations={calibrations} />}
      </div>
    </div>
  );
}
